import { Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { SocketService } from 'src/app/viewbid/shared/services/socket.service';
import { ServerClockService } from 'src/app/viewbid/shared/services/server-clock.service';
import { AuctionCatalogsService, GetAuctionById200Response } from '@viewbid/ts-angular-client';

interface TimeUpdate {
    auctionLotId: string;
    endDate: string;
}

@Injectable({
    providedIn: 'root',
})
export class TimerService {
    private timerCompleteSubject = new Subject<string>();
    timerComplete$ = this.timerCompleteSubject.asObservable();

    private startTimerCompleteSubject = new Subject<string>();
    startTimerComplete$ = this.startTimerCompleteSubject.asObservable();

    private startTimerCompleteForAuctionSubject = new Subject<string>();
    private closingTimerCompleteForAuctionSubject = new Subject<string>();
    private snipeWindowTimerCompleteForAuctionSubject = new Subject<string>();
    private endTimerCompleteForAuctionSubject = new Subject<string>();
    startTimerCompleteForAuction$ =
        this.startTimerCompleteForAuctionSubject.asObservable();
    closingTimerCompleteForAuction$ =
        this.closingTimerCompleteForAuctionSubject.asObservable();
    snipeWindowTimerCompleteForAuction$ =
        this.snipeWindowTimerCompleteForAuctionSubject.asObservable();
    endTimerCompleteForAuction$ =
        this.endTimerCompleteForAuctionSubject.asObservable();
    private activeTimers = new Map<string, ReturnType<typeof setInterval>>();
    private processedEndTimes = new Map<string, string>();

    // Add new subject for timer reset events
    private timerResetSubject = new Subject<{id: string, newEndTime: string}>();
    timerReset$ = this.timerResetSubject.asObservable();

    hasProcessedEndTime(auctionLotId: string, endTime: string): boolean {
        return this.processedEndTimes.get(auctionLotId) === endTime;
    }

    notifyTimerComplete(auctionLotId: string): void {
        this.timerCompleteSubject.next(auctionLotId);
    }

    notifyStartTimerComplete(auctionLotId: string): void {
        // Verify auction times with API when transitioning from upcoming to active
        this.auctionService.getAuctionById(auctionLotId).subscribe({
            next: (response: GetAuctionById200Response) => {
                // Update end time if changed
                if (response.data.toDatetime) {
                    this.resetTimer(auctionLotId, response.data.toDatetime);
                }
                this.startTimerCompleteSubject.next(auctionLotId);
            },
            error: () => {
                // Still notify even if API call fails
                this.startTimerCompleteSubject.next(auctionLotId);
            }
        });
    }

    notifyStartTimerCompleteForAuction(auctionFromDate: string, auctionId: string): void {
        // Keyed separately from the end timer so both can run for one auction
        this.scheduleAuctionEventTimer(`start:${auctionId}`, auctionFromDate, () =>
            this.startTimerCompleteForAuctionSubject.next(auctionId),
        );
    }

    notifyClosingTimerCompleteForAuction(firstLotCloseTime: string, auctionId: string): void {
        this.scheduleAuctionEventTimer(`closing:${auctionId}`, firstLotCloseTime, () =>
            this.closingTimerCompleteForAuctionSubject.next(auctionId),
        );
    }

    notifySnipeWindowTimerCompleteForAuction(snipeWindowStart: string, auctionId: string): void {
        this.scheduleAuctionEventTimer(`snipe-window:${auctionId}`, snipeWindowStart, () =>
            this.snipeWindowTimerCompleteForAuctionSubject.next(auctionId),
        );
    }

    notifyEndTimerCompleteForAuction(auctionToDate: string, auctionId: string): void {
        // Keyed by the bare auctionId so clearTimer(auctionId) keeps working
        this.scheduleAuctionEventTimer(auctionId, auctionToDate, () =>
            this.endTimerCompleteForAuctionSubject.next(auctionId),
        );
    }

    private scheduleAuctionEventTimer(
        timerKey: string,
        targetDatetime: string,
        notify: () => void,
    ): void {
        // If we've already processed this target time for this key, skip
        if (this.processedEndTimes.get(timerKey) === targetDatetime) {
            return;
        }

        // Track that we've processed this target time
        this.processedEndTimes.set(timerKey, targetDatetime);

        const targetTime: number = Math.trunc(new Date(targetDatetime).getTime() / 1000);
        const now: number = this.nowSeconds();

        // If the target time has already passed, notify immediately
        if (now >= targetTime) {
            notify();
            return;
        }

        // Clear any existing timer
        if (this.activeTimers.has(timerKey)) {
            clearInterval(this.activeTimers.get(timerKey));
            this.activeTimers.delete(timerKey);
        }

        // Start new timer
        const interval: ReturnType<typeof setInterval> = setInterval(() => {
            if (this.nowSeconds() >= targetTime) {
                clearInterval(interval);
                notify();
                this.activeTimers.delete(timerKey);
            }
        }, 1000);

        this.activeTimers.set(timerKey, interval);
    }

    // Reset timer with new end time
    resetTimer(auctionLotId: string, newEndTime: string): void {
        const currentTime: Date = new Date(this.serverClock.now());
        const newEndDate: Date = new Date(newEndTime);

        // Always allow reset if:
        // 1. No previous end time exists
        // 2. New end time is different from current
        // 3. New end time is in the future
        const shouldReset: boolean = !this.processedEndTimes.has(auctionLotId) ||
            this.processedEndTimes.get(auctionLotId) !== newEndTime ||
            newEndDate > currentTime;

        if (shouldReset) {
            // Track the new end time
            this.processedEndTimes.set(auctionLotId, newEndTime);

            // Notify subscribers of the new end time
            this.timerResetSubject.next({id: auctionLotId, newEndTime});

            // Clear any existing timer
            if (this.activeTimers.has(auctionLotId)) {
                clearInterval(this.activeTimers.get(auctionLotId));
                this.activeTimers.delete(auctionLotId);
            }

            // Only start new timer if end time is in the future
            if (newEndDate > currentTime) {
                const endTime: number = Math.trunc(newEndDate.getTime() / 1000);
                const interval: ReturnType<typeof setInterval> = setInterval(() => {
                    if (this.nowSeconds() >= endTime) {
                        clearInterval(interval);
                        this.timerCompleteSubject.next(auctionLotId);
                        this.activeTimers.delete(auctionLotId);
                    }
                }, 1000);

                this.activeTimers.set(auctionLotId, interval);
            } else {
                // If end time is in the past, notify immediately
                this.timerCompleteSubject.next(auctionLotId);
            }
        }
    }

    clearTimer(auctionId: string): void {
        if (this.activeTimers.has(auctionId)) {
            clearInterval(this.activeTimers.get(auctionId));
            this.activeTimers.delete(auctionId);
        }
    }

    releaseTimer(auctionId: string): void {
        this.clearTimer(auctionId);
        this.processedEndTimes.delete(auctionId);
    }

    constructor(
        private socketService: SocketService,
        private auctionService: AuctionCatalogsService,
        private serverClock: ServerClockService,
    ) {}

    private nowSeconds(): number {
        return Math.trunc(this.serverClock.now() / 1000);
    }

    private processTimeUpdate(
        lotId: string,
        endDate: string,
        resourceIds: string[],
        onEndTimeUpdate?: (newEndTime: string) => void
    ): void {
        const matchingResourceId: string | undefined = resourceIds.find((id: string) => lotId === id);

        if (matchingResourceId && endDate && !this.hasProcessedEndTime(matchingResourceId, endDate)) {
            if (onEndTimeUpdate) {
                onEndTimeUpdate(endDate);
            }
            this.resetTimer(matchingResourceId, endDate);
        }
    }

    /**
     * Subscribes to socket events for resources and handles timer reset events.
     * The caller owns the returned subscription and must unsubscribe it.
     */
    subscribeToTimerEvents(
        resourceIds: string[],
        onEndTimeUpdate?: (newEndTime: string) => void
    ): Subscription {
        this.socketService.subscribeToResources(
            resourceIds.map((id: string) => ({ resourceId: id, listen: true }))
        );

        return this.socketService.resource$.subscribe((data: string) => {
            const eventResponse: any = JSON.parse(data);

            let updates: TimeUpdate[] = [];

            if (eventResponse.action === 'PAGE_LOAD') {
                updates = [
                    {
                        auctionLotId: eventResponse.data?.auctionLotsId,
                        endDate: eventResponse.data?.endDate,
                    },
                ];
            } else if (eventResponse.action === 'UPDATE_TIMES_EVENT') {
                updates = eventResponse.data.lots;
            }

            if (updates) {
                updates.forEach((update: TimeUpdate) => {
                    if (update.auctionLotId && update.endDate) {
                        this.processTimeUpdate(
                            update.auctionLotId,
                            update.endDate,
                            resourceIds,
                            onEndTimeUpdate,
                        );
                    }
                });
            }
        });
    }
}
